import React from 'react';
import { motion } from 'framer-motion';
import ResultsTableRow from './ResultsTableRow/ResultsTableRow';
import ResultsMobileCard from './ResultsMobileCard/ResultsMobileCard';
import { Result } from '../../../../../services/serpApi';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '../../../../../components/CustomCheckbox/CustomCheckbox';

interface ResultsTableProps {
  results: Result[];
  selectedResults: number[];
  setSelectedResults: (ids: number[]) => void;
  onDeleteResult: (id: number) => void; // Nova prop
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  selectedResults,
  setSelectedResults,
  onDeleteResult,
}) => {
  const { t } = useTranslation();

  const handleRowSelect = (id: number) => {
    if (selectedResults.includes(id)) {
      setSelectedResults(selectedResults.filter(resultId => resultId !== id));
    } else {
      setSelectedResults([...selectedResults, id]);
    }
  };

  // Lógica para o checkbox "Marcar/Desmarcar Todos" na página atual
  const allDisplayedSelected = results.length > 0 && results.every(r => selectedResults.includes(r.id));
  // const someDisplayedSelected = results.some(r => selectedResults.includes(r.id)) && !allDisplayedSelected;

  const handleSelectAll = () => {
    if (allDisplayedSelected) {
      // Desmarcar todos na página atual
      setSelectedResults(selectedResults.filter(id => !results.map(r => r.id).includes(id)));
    } else {
      // Marcar todos na página atual
      const newSelectedIds = results.map(r => r.id).filter(id => !selectedResults.includes(id));
      setSelectedResults([...selectedResults, ...newSelectedIds]);
    }
  };

  return (
    <motion.div
      className="overflow-x-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <div className="md:hidden p-4 bg-gray-50 dark:bg-gray-800/20">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2">
            <CustomCheckbox
              checked={allDisplayedSelected}
              onChange={handleSelectAll}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('Selecionar todos')}</span>
          </div>
        </div>
        {results.map((result) => (
          <ResultsMobileCard
            key={result.id}
            result={result}
            isSelected={selectedResults.includes(result.id)}
            onSelect={() => handleRowSelect(result.id)}
            onDelete={onDeleteResult}
          />
        ))}
      </div>

      <table className="w-full text-left hidden md:table">
        <thead className="border-b border-gray-200 dark:border-primary-darkMode bg-gray-50 dark:bg-background-dark-elevated">
          <tr>
            <th className="p-4 w-8">
              <CustomCheckbox
                checked={allDisplayedSelected}
                onChange={handleSelectAll}
              // Adicionando um indicador visual para seleção parcial (não suportado nativamente pelo CustomCheckbox, mas podemos simular)
              // Para simplificar, vamos usar apenas checked/unchecked.
              />
            </th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white">{t('dashboard.table_header_company')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white">{t('dashboard.table_header_contact')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white">{t('dashboard.table_header_location')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white text-center">{t('dashboard.table_header_whatsapp')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white text-center">{t('dashboard.table_header_rating')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white text-center">{t('dashboard.table_header_prospected')}</th>
            <th className="p-4 text-sm font-semibold text-gray-500 dark:text-white">{t('dashboard.table_header_actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
          {results.map((result, index) => (
            <ResultsTableRow
              key={result.id}
              result={result}
              isSelected={selectedResults.includes(result.id)}
              onSelect={() => handleRowSelect(result.id)}
              onDelete={onDeleteResult} // Passando o handler
              index={index}
            />
          ))}
        </tbody>
      </table>
    </motion.div>
  );
};

export default ResultsTable;